/**
 * assets/pdp.js
 * PDP interactivity for the Animated Glow Belt product page.
 * Loaded globally as type="module" from theme.liquid — no-op on non-product pages.
 */
import { PRESETS as LIBRARY } from 'nion-fx';

// The Signature looks here; the full library lives on the homepage and in the app.
const PRESETS = LIBRARY.filter(p => p.family === 'Signature');

// ── Element refs ──────────────────────────────────────────────────────────
const glowBelt   = document.getElementById('pdp-glow-belt');
const form       = document.getElementById('pdp-form');
const variantIn  = document.getElementById('pdp-variant-id');
const presetIn   = document.getElementById('pdp-preset-input');
const qtyIn      = document.getElementById('pdp-qty-input');
const qtyDisplay = document.getElementById('pdp-qty-display');
const atcBtn     = document.getElementById('pdp-atc-btn');
const presetDots = document.getElementById('pdp-preset-dots');
const chipsEl    = document.querySelector('.pdp-chips');

// Guard: if pdp elements are absent this is not the product page — do nothing
if (glowBelt && form && chipsEl && presetDots) {
  init();
}

function init() {

  // ── 1. Build preset chip strip ──────────────────────────────────────────
  PRESETS.forEach((preset, i) => {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'pdp-chip' + (i === 0 ? ' pdp-chip--active' : '');
    chip.dataset.id = preset.id;
    chip.dataset.index = String(i);
    chip.setAttribute('aria-label', preset.name);

    const swatch = document.createElement('span');
    swatch.className = 'pdp-chip__swatch';
    swatch.style.background = preset.swatch[0];
    swatch.style.boxShadow = `0 0 6px ${preset.swatch[0]}`;

    chip.appendChild(swatch);
    chip.appendChild(document.createTextNode(preset.name));
    chip.addEventListener('click', () => glowBelt.setPreset(preset.id));
    chipsEl.appendChild(chip);
  });

  // ── 2. Build preset dot picker (first 8 presets only) ─────────────────
  PRESETS.slice(0, 8).forEach((preset, i) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'pdp-dot' + (i === 0 ? ' pdp-dot--active' : '');
    dot.dataset.index = String(i);
    dot.style.background = preset.swatch[0];
    dot.style.boxShadow = `0 0 8px ${preset.swatch[0]}66`;
    dot.setAttribute('aria-label', preset.name);
    dot.addEventListener('click', () => glowBelt.setPreset(preset.id));
    presetDots.appendChild(dot);
  });

  // ── 3. Set initial live badge ──────────────────────────────────────────
  const nameBadge = document.querySelector('.pdp-live-badge--name');
  if (nameBadge && PRESETS[0]) {
    nameBadge.textContent = '▸ ' + PRESETS[0].name.toUpperCase();
  }

  // ── 4. Preset sync via preset-change CustomEvent ───────────────────────
  glowBelt.addEventListener('preset-change', (e) => {
    const { id, preset } = e.detail;
    const index = PRESETS.findIndex(p => p.id === id);

    // Chip strip counter
    const counter = document.querySelector('.pdp-chip-counter');
    if (counter) counter.textContent = `${index + 1} / ${PRESETS.length}`;

    // Active chip
    document.querySelectorAll('.pdp-chip').forEach(c => {
      c.classList.toggle('pdp-chip--active', c.dataset.id === id);
    });

    // Active dot (indices 8–11 intentionally make all dots inactive)
    document.querySelectorAll('.pdp-dot').forEach((d, i) => {
      d.classList.toggle('pdp-dot--active', i === index);
    });

    // Labels
    const presetLabel = document.getElementById('pdp-preset-label');
    if (presetLabel) presetLabel.textContent = preset.name;
    if (nameBadge)   nameBadge.textContent = '▸ ' + preset.name.toUpperCase();

    // Hidden form input (sent as line item property on ATC)
    if (presetIn) presetIn.value = preset.name;
  });

  // ── 5. Gallery switching ───────────────────────────────────────────────
  document.querySelectorAll('[data-thumb]').forEach(btn => {
    btn.addEventListener('click', () => switchMedia(btn.dataset.thumb));
  });

  function switchMedia(index) {
    document.querySelectorAll('.pdp-media').forEach((el, i) => {
      el.hidden = String(i) !== String(index);
    });
    document.querySelectorAll('[data-thumb]').forEach((btn, i) => {
      btn.classList.toggle('pdp-thumb--active', String(i) === String(index));
    });
  }

  // ── 6. Variant / size selection ────────────────────────────────────────
  document.querySelectorAll('.pdp-size-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      document.querySelectorAll('.pdp-size-btn').forEach(b =>
        b.classList.remove('pdp-size-btn--active')
      );
      btn.classList.add('pdp-size-btn--active');
      if (variantIn) variantIn.value = btn.dataset.variantId;

      const sizeLabel = document.getElementById('pdp-selected-size');
      if (sizeLabel) sizeLabel.textContent = btn.dataset.variantTitle;

      if (atcBtn) {
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to cart';
      }
    });
  });

  // ── 7. Quantity stepper ────────────────────────────────────────────────
  let qty = 1;

  document.getElementById('pdp-qty-inc')?.addEventListener('click', () => {
    qty = Math.min(qty + 1, 10);
    if (qtyDisplay) qtyDisplay.textContent = qty;
    if (qtyIn) qtyIn.value = qty;
  });
  document.getElementById('pdp-qty-dec')?.addEventListener('click', () => {
    qty = Math.max(qty - 1, 1);
    if (qtyDisplay) qtyDisplay.textContent = qty;
    if (qtyIn) qtyIn.value = qty;
  });

  // ── 8. ATC fetch ───────────────────────────────────────────────────────
  // POST to /cart/add.js with sections array → Shopify returns rendered section
  // HTML in the same response. Pass the full response to drawer.renderContents()
  // which reads state.id and state.sections['cart-drawer'].
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!atcBtn || atcBtn.disabled) return;

    const body = {
      id:       parseInt(variantIn ? variantIn.value : '0'),
      quantity: parseInt(qtyIn ? qtyIn.value : '1'),
      properties: {
        _default_preset: presetIn ? presetIn.value : '',
      },
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    };

    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding…';

    // routes.cart_add_url is /cart/add, which answers with a 302 to the cart
    // page (HTML); the .js endpoint returns JSON.
    fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then(r => { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(() => {
        // The item is in the cart now. Go to the cart page so the customer
        // sees it; the drawer/notification paths never showed anything here
        // and the header badge didn't update.
        atcBtn.textContent = '✓ Added to cart';
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      })
      .catch(() => {
        atcBtn.disabled = false;
        atcBtn.textContent = 'Try again';
        setTimeout(() => { atcBtn.textContent = 'Add to cart'; }, 2200);
      });
  });

  // ── 9. Accordion — one item open at a time ─────────────────────────────
  document.querySelectorAll('.pdp-accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      // Close all
      document.querySelectorAll('.pdp-accordion-trigger').forEach(t => {
        t.setAttribute('aria-expanded', 'false');
        const body = t.nextElementSibling;
        if (body) body.hidden = true;
      });
      // Open this one if it was closed
      if (!isOpen) {
        trigger.setAttribute('aria-expanded', 'true');
        const body = trigger.nextElementSibling;
        if (body) body.hidden = false;
      }
    });
  });

} // end init()
